import React, { useMemo, useLayoutEffect, useCallback, useRef } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { Table, ColumnDef } from "@/components/NewTable";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { useSupervisors, SupervisorType } from "../SupervisorsContext";
import { shiftsService } from "@/services/shiftService";
import { useToolTip } from "@/hooks/useToolTip";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";

type RootStackParamList = {
  AssignedSupervisors: undefined;
  AddSupervisors: undefined;
};

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  disabled?: boolean;
}

function HeaderButton({ icon, tooltip, onPress, disabled }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    if (disabled) return;
    isHovering.current = true;
    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      const tooltipWidth = tooltip.length * 7 + 16;
      const tooltipHeight = 28;
      toolTip.setLocation({
        width: tooltipWidth,
        height: tooltipHeight,
        x: x + width - tooltipWidth,
        y: y - tooltipHeight - 6,
      });
      toolTip.setToolTipContent(
        <View
          style={{
            backgroundColor: colors.background,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 8,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: colors.border,
          }}
        >
          <ThemedText style={{ fontSize: 12 }}>{tooltip}</ThemedText>
        </View>,
      );
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip, disabled]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    if (disabled) return;
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip, disabled]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4, opacity: disabled ? 0.4 : 1 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      disabled={disabled}
    >
      <MaterialCommunityIcons name={icon} size={24} color={disabled ? colors.textSecondary : colors.primary} />
    </Pressable>
  );
}

export default function AssignedSupervisorsScreen() {
  const { colors } = useTheme();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { supervisors, loading, shiftId, removeSupervisor } = useSupervisors();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const { navigateToEntity } = useNavigationGuard();

  // Navigation handler for supervisor names
  const handleUserPress = useCallback(
    (row: SupervisorType) => {
      // Save current entity to navigation history before navigating
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      // Navigate to user profile
      navigateToEntity("user", row.user_id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const formatSupervisorName = (supervisor: SupervisorType) => {
    const user = supervisor.user;
    if (!user) return "—";
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || "—";
  };

  const handleRemoveSupervisor = useCallback(
    async (supervisor: SupervisorType) => {
      if (!shiftId) return;

      try {
        await shiftsService.removeShiftSupervisor(shiftId, supervisor.user_id);
        removeSupervisor(supervisor.user_id);
      } catch (err) {
        console.error("Failed to remove supervisor:", err);
      }
    },
    [shiftId, removeSupervisor],
  );

  // Set up header
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <View style={styles.headerTitleText}>
            <ThemedText style={styles.title}>Supervisors</ThemedText>
            <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
              {supervisors.length} assigned
            </ThemedText>
          </View>
        </View>
      ),
      headerRight: () => (
        <HeaderButton icon="plus" tooltip="Add Supervisor" onPress={() => navigation.navigate("AddSupervisors")} />
      ),
    });
  }, [navigation, supervisors.length, colors]);

  const columns: ColumnDef<SupervisorType>[] = useMemo(
    () => [
      {
        key: "avatar",
        header: "",
        width: 48,
        align: "center",
        sortable: false,
        searchable: false,
        render: (_, row) => (
          <Avatar
            userId={row.user_id}
            email={row.user?.email_address ?? undefined}
            firstName={row.user?.preferred_name || row.user?.first_name || ""}
            lastName={row.user?.last_name || ""}
            size={30}
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 150,
        accessor: (row) => formatSupervisorName(row),
        onPress: handleUserPress,
      },
      {
        key: "eligible",
        header: "Eligible",
        width: 80,
        align: "center",
        searchable: false,
        accessor: (row) => ((row.user?.meta_data as any)?.supervisor_eligible ? 1 : 0),
        render: (_, row) => {
          const isEligible = (row.user?.meta_data as any)?.supervisor_eligible === true;
          return isEligible ? (
            <MaterialCommunityIcons name="check-circle" size={18} color={colors.complete.border} />
          ) : (
            <ThemedText style={{ fontSize: 12, opacity: 0.5 }}>—</ThemedText>
          );
        },
      },
      {
        key: "email",
        header: "Email",
        flex: 1,
        minWidth: 200,
        accessor: (row) => row.user?.email_address || "—",
      },
    ],
    [handleUserPress, colors.complete.border],
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      <Table<SupervisorType>
        data={supervisors}
        columns={columns}
        keyExtractor={(item) => item.user_id}
        loading={loading}
        loadingRowCount={3}
        sortable
        defaultSort={{ key: "name", direction: "asc" }}
        emptyMessage="No supervisors assigned"
        emptyIcon="account-tie-outline"
        rowActions={[
          {
            icon: "trash-can-outline",
            label: "Remove",
            onPress: (row) => handleRemoveSupervisor(row),
          },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitleText: {
    gap: 2,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 12,
  },
});
